import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdCollapsibleItems } from '../bodygraph/advanced-properties/hd-collapsible-items';
import { HdCycleCardsComponent } from '../bodygraph/advanced-properties/hd-cycle-cards.component';
import {
  HdCycleCenterBlockCause,
  HdCycleCenterBlockInfoRequest,
  HdCycleConditionedCenterBlockComponent,
} from '../bodygraph/advanced-properties/hd-cycle-conditioned-center-block.component';
import { HdCycleHangingGateBlockComponent } from '../bodygraph/advanced-properties/hd-cycle-hanging-gate-block.component';
import { HdCycleSectionHeaderComponent } from '../bodygraph/advanced-properties/hd-cycle-section-header.component';
import {
  HdChannelListChannelInfoRequest,
  HdChannelListGateInfoRequest,
  HdChannelListItemComponent,
} from '../bodygraph/channel-list/hd-channel-list-item.component';
import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HdBodygraphCenter } from '../bodygraph/hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdCycleCardSlot, HdCycleCardSource } from '../bodygraph/hd-cycle-cards.builder';
import { HdCycleChannelVariant, HdCycleChannelVm, HdCycleHangingGateVm } from '../bodygraph/hd-cycle-keynotes.builder';
import {
  HD_CYCLE_ENCOUNTER_KIND_FA_ICONS,
  HD_CYCLE_MAIN_KEYNOTES,
  HdCycleMainKeynoteKey,
} from '../bodygraph/hd-cycles-cheat-sheet-metadata';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../bodygraph/hd-planet-info.service';
import { HdChartSettingsStore } from '../bodygraph/chart-tabs/hd-chart-settings.store';
import { IChingLinePosition } from '../../iching/core/iching.models';
import { HdIChingAccessService } from '../../iching/hd-iching-access.service';
import { HdIChingDayTarget } from '../../iching/panel/hd-iching-panel.models';
import { HdIChingPanelService } from '../../iching/panel/hd-iching-panel.service';
import { HD_JUST_NOW_ICONS } from '../library/hd-chart-cycle-metadata';
import { HdJustNowPanelStateService } from './hd-just-now-panel-state.service';
import { HdJustNowSectionKey, buildHdJustNowPanelModel } from './hd-just-now-panel.builder';

const T = 'humanDesign.chart.justNowPanel';
/** Dock-tab-label en paneelkop; vaste eigennaam, zoals de shortcut en de Cycles-sectie. */
export const HD_JUST_NOW_PANEL_TITLE = 'Just Now';
/** Header-kleur van de Vernieuwing-sectie: bewust gedempt grijs (geen cyclusthema), zoals in het Geavanceerd-paneel. */
const RENEWAL_SECTION_COLOR = '#8A8F98';
const MOMENT_FORMAT = 'D MMMM YYYY, HH:mm';

interface ConditionedCenterVm {
  readonly center: HdBodygraphCenter;
  readonly label: string;
  /** HTML met `<b>`-accenten (cyclus-specifiek niet-zelf thema uit de cycles-scope). */
  readonly text: string;
  readonly causes: readonly HdCycleCenterBlockCause[];
}

type ItemVm =
  | {
      readonly kind: 'channel';
      readonly key: string;
      readonly channel: HdCycleChannelVm;
      readonly variantLabel: string;
      readonly cards: readonly HdCycleCardSlot[];
    }
  | {
      readonly kind: 'gate';
      readonly key: string;
      readonly hanging: HdCycleHangingGateVm;
      readonly cards: readonly HdCycleCardSlot[];
    };

interface SectionVm {
  readonly key: HdJustNowSectionKey;
  readonly color: string;
  readonly faIcon: string;
  readonly title: string;
  readonly bringsLabel: string;
  readonly brings: string;
  readonly bullets: readonly string[];
  readonly items: readonly ItemVm[];
  /** Keynote-accent op de kanaal-items; null voor de vernieuwings-sectie. */
  readonly channelKeynoteKey: HdCycleMainKeynoteKey | null;
  /** Direct accent op de kanaal-items (vernieuwings-sectie); wint van keynoteKey. */
  readonly channelAccent: { color: string; faIcon: string } | null;
  readonly muted: boolean;
}

/**
 * Just Now-paneel (spec 2026-10-14): gedockt naast de Just Now-transit-overlay
 * op een standaardchart. Combineert de cyclusmechanica van het moment (zelfde
 * indeling als het Geavanceerd-paneel van een cycle-/momentchart: niet-zelf
 * center thema's, Persoonlijk / Educatief / Omgevend / Vernieuwing) met het
 * neutrino-weer per planeet: onder elk kanaal- of poort-item de I Tjing-
 * lijnkaart(en) van de transitpoort(en) (`hd-cycle-cards`, dezelfde kaarten
 * als in het Geavanceerd-paneel, hier **default open**). Geordend als
 * oplijsting van mechanieken (planeetprioriteit), niet als tijdsverloop; de
 * sectiekoppen zijn sticky zodat de context van een kaart zichtbaar blijft.
 *
 * Alle data komt via {@link HdJustNowPanelStateService} van de chart-pagina;
 * doorklikken (poort, kanaal, center) lopen via haar handlers.
 */
@Component({
  selector: 'hd-just-now-panel',
  templateUrl: './hd-just-now-panel.component.html',
  styleUrl: './hd-just-now-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HdCycleSectionHeaderComponent,
    HdCycleConditionedCenterBlockComponent,
    HdCycleHangingGateBlockComponent,
    HdChannelListItemComponent,
    HdCycleCardsComponent,
  ],
})
export class HdJustNowPanelComponent {
  protected readonly state = inject(HdJustNowPanelStateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);
  private readonly settings = inject(HdChartSettingsStore);

  protected readonly title = HD_JUST_NOW_PANEL_TITLE;
  protected readonly titleIcon = HD_JUST_NOW_ICONS.transit;

  /** Uitklapstaat van de kaarten per item: hier default open (het paneel ís het neutrino-weer). */
  protected readonly items = new HdCollapsibleItems(true);

  constructor() {
    // Beide content-talen eager laden zodat de NL-fallback voor nog niet
    // vertaalde EN-velden meteen correcte tekst oplevert (zoals de keynote-secties).
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly active = computed(() => this.state.momentUtc() != null);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const t = (key: string) => this.transloco.translate<string>(`${T}.${key}`, undefined, lang);
    return {
      momentLabel: t('momentLabel'),
      chartLabel: t('chartLabel'),
      ichingNow: t('ichingNow'),
      intro: t('intro'),
      centersEmpty: t('centersEmpty'),
      empty: t('empty'),
      inactive: t('inactive'),
      centersTitle: this.cyclesText('centerConditioning.momentTitle', lang),
    };
  });

  /** Het getoonde moment in lokale tijd (volgt de tijdslider), in de content-taal. */
  protected readonly momentText = computed(() => {
    const utc = this.state.momentUtc();
    if (utc == null) {
      return '';
    }
    return moment.utc(utc).local().locale(momentLocaleFor(this.lang())).format(MOMENT_FORMAT);
  });

  /** Classic I Ching-icoon in de kop (feature + setting + gebiedssetting transits). */
  protected readonly ichingIconsVisible = computed(
    () => this.ichingAccess.classicVisible() && this.settings.classicIChingIconsTransits(),
  );

  /** Hexagram van dit moment: zon (planetId 0) en maan (planetId 2) uit de transitactivaties. */
  protected readonly ichingDayTarget = computed<HdIChingDayTarget | undefined>(() => {
    const utc = this.state.momentUtc();
    const activations = this.state.transitActivations();
    const sun = activations.find(a => a.planetId === 0);
    const moon = activations.find(a => a.planetId === 2);
    if (utc == null || !sun) {
      return undefined;
    }
    const target: HdIChingDayTarget = {
      view: 'day',
      dateKey: moment.utc(utc).local().format('YYYY-MM-DD'),
      momentLabel: this.momentText(),
      sun: { gate: sun.gate, line: sun.line as IChingLinePosition },
    };
    return moon ? { ...target, moon: { gate: moon.gate, line: moon.line as IChingLinePosition } } : target;
  });

  protected openIChingDay(event: MouseEvent): void {
    const target = this.ichingDayTarget();
    if (target) {
      this.ichingPanel.openDay(target, { x: event.clientX, y: event.clientY });
    }
  }

  /** Het pure model; herrekent enkel bij een andere natal chart of transitstand. */
  private readonly model = computed(() =>
    buildHdJustNowPanelModel({
      natalConfiguration: this.state.natalConfiguration(),
      natalPlanetaryActivations: this.state.natalPlanetaryActivations(),
      transitActivations: this.state.transitActivations(),
      renderStateFactory: this.renderStateFactory,
    }),
  );

  protected readonly conditionedCenters = computed<readonly ConditionedCenterVm[]>(() => {
    const lang = this.lang();
    return this.model().conditionedCenters.map(item => ({
      center: item.center,
      label: this.cyclesText(`centerConditioning.centers.${item.center}.label`, lang),
      text: this.cyclesText(`centerConditioning.centers.${item.center}.text`, lang),
      causes: item.causes.map(cause => ({
        glyph: cause.glyph,
        gate: cause.gate,
        line: cause.line,
        title: `${this.planetName(cause, lang)} ${cause.gate}.${cause.line}`,
      })),
    }));
  });

  protected readonly sections = computed<readonly SectionVm[]>(() => {
    const lang = this.lang();
    const bringsLabel = this.cyclesText('bringsLabel', lang);
    return this.model().sections.map(section => {
      const muted = section.key === 'renewal';
      const meta = muted
        ? { color: RENEWAL_SECTION_COLOR, faIcon: HD_CYCLE_ENCOUNTER_KIND_FA_ICONS.renewal }
        : HD_CYCLE_MAIN_KEYNOTES.find(candidate => candidate.key === section.key);
      const color = meta?.color ?? '#3D414E';
      const faIcon = meta?.faIcon ?? '';
      return {
        key: section.key,
        color,
        faIcon,
        title: this.cyclesText(`mainKeynotes.${section.key}.title`, lang),
        bringsLabel,
        brings: this.cyclesText(`mainKeynotes.${section.key}.brings`, lang),
        bullets: this.cyclesList(`mainKeynotes.${section.key}.bullets`, lang),
        channelKeynoteKey: muted ? null : (section.key as HdCycleMainKeynoteKey),
        channelAccent: muted ? { color, faIcon } : null,
        muted,
        items: section.items.map<ItemVm>(item =>
          item.kind === 'channel'
            ? {
                kind: 'channel',
                key: item.key,
                channel: item.channel,
                variantLabel: this.variantLabel(item.variant, lang),
                cards: item.cards,
              }
            : { kind: 'gate', key: item.key, hanging: item.hanging, cards: item.cards }),
      };
    });
  });

  protected readonly isEmpty = computed(() => this.active() && this.sections().length === 0);

  /** Uitgeklapte sectie-bullets (sectie-key ⇔ open), zoals in het Geavanceerd-paneel. */
  private readonly expandedSections = signal<ReadonlySet<HdJustNowSectionKey>>(new Set());

  protected isExpanded(key: HdJustNowSectionKey): boolean {
    return this.expandedSections().has(key);
  }

  protected toggleSection(key: HdJustNowSectionKey): void {
    this.expandedSections.update(expanded => {
      const next = new Set(expanded);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  protected requestGateInfo(request: HdChannelListGateInfoRequest): void {
    this.state.requestHandlers()?.openGateInfo(request);
  }

  protected requestChannelInfo(request: HdChannelListChannelInfoRequest): void {
    this.state.requestHandlers()?.openChannelInfo(request);
  }

  protected requestCenterInfo(request: HdCycleCenterBlockInfoRequest): void {
    this.state.requestHandlers()?.openCenterInfo(request);
  }

  /**
   * Verwijsregel: scroll de volledige kaart (hoger in het paneel) in beeld,
   * onder de sticky kop. Staat het item van die kaart dichtgeklapt, dan eerst
   * openklappen (de kaart bestaat anders niet in de DOM).
   */
  protected scrollToCard(anchorId: string): void {
    const ownerKey = this.cardOwnerKey(anchorId);
    if (ownerKey != null && !this.items.isExpanded(ownerKey)) {
      this.items.toggle(ownerKey);
      // Na de render van het uitgeklapte item scrollen.
      requestAnimationFrame(() => this.scrollToCardElement(anchorId));
      return;
    }
    this.scrollToCardElement(anchorId);
  }

  private scrollToCardElement(anchorId: string): void {
    HdCycleCardsComponent.findCard(this.host.nativeElement, anchorId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Het item dat de volledige kaart (`first`) van dit anker draagt. */
  private cardOwnerKey(anchorId: string): string | undefined {
    for (const section of this.model().sections) {
      for (const item of section.items) {
        if (item.cards.some(card => card.first && card.anchorId === anchorId)) {
          return item.key;
        }
      }
    }
    return undefined;
  }

  private planetName(source: HdCycleCardSource, lang: string): string {
    return this.planetInfo.planetInfoIn(source.planetKey, lang)?.name ?? source.planetKey;
  }

  private variantLabel(variant: HdCycleChannelVariant, lang: string): string {
    const key = variant === 'companionship' ? 'renewal' : variant;
    return this.transloco.translate(`${T}.variants.${key}`, undefined, lang);
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  private cyclesList(key: string, lang: string): readonly string[] {
    const value = this.gateInfo.cyclesCheatSheetListIn(key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetListIn(key, 'nl');
  }
}
